import { Injectable } from '@angular/core';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { CostAssumptionsLayout } from '../types/tvd-types';

/**
 * Resolves the layout for cost assumptions payload construction.
 *
 * Layout options:
 * - 'flat' → single set of rates
 * - 'byCountry' → rates scoped per country
 *
 * Rules:
 * - 'byCountry' ONLY when:
 *     • At least two countries are explicitly selected
 *     • AND all selected regions support country-level assumptions
 *
 * - Otherwise → 'flat'
 *
 * Notes:
 * - Regions alone do NOT imply country-scoped layout
 * - Defensive defaults ensure stability when filters are undefined
 * 
 * MIGRATION NOTE:
 * Originally implemented as CostAssumptionsLayoutService.
 * Refactored into a resolver to clarify intent as pure decision logic.
 * 
 * TODO (API-driven configuration):
 * Replace hardcoded region rules (Set<string>) with backend-driven configuration.
 * Supported layouts and region capabilities should come from API (Swagger/OpenAPI),
 * allowing frontend behavior to be driven entirely by backend contracts.
 * 
 * Desired end state:
 * - Backend defines which regions support country-scoped assumptions
 * - Swagger generates typed contract
 * - Resolver consumes API-provided config instead of static Set
 */
@Injectable({ providedIn: 'root' })
export class CostAssumptionsLayoutResolver {

  /**
   * Regions that support country-scoped assumptions
   * Extend this list as backend capabilities evolve
   * 
   * TODO (API-driven configuration):
   * Replace this Set with backend-provided capability flags.
   * Example future shape:
   *   regionCapabilities[region].supportsCountryScope === true
   */
  private readonly regionsSupportingCountryScope = new Set<string>([
    'AP',
    'LA',
    'IMEA'
  ]);

  constructor(private _filtersService: FiltersService) {}

  /**
   * Resolve layout using current filters from FiltersService
   */
  resolve(): CostAssumptionsLayout {
    const filters = this._filtersService.selectedFilters?.getValue();
    return this.resolveFromFilters(filters);
  }

  /**
   * Resolve layout using explicit filters (useful for testing or non-reactive flows)
   */
  resolveFromFilters(filters: ContractModel | undefined): CostAssumptionsLayout {

    // Defensive: no filters → flat
    if (!filters?.regions?.length) {
      return 'flat';
    }

    // Check if ALL regions support country-scoped assumptions
    const supportsCountryScope = filters.regions.every(region =>
      this.regionsSupportingCountryScope.has(region)
    );

    // Count selected countries
    const countryCount = filters.countries?.length ?? 0;

    // Apply rules
    if (supportsCountryScope && countryCount > 1) {
      return 'byCountry';
    }

    return 'flat';
  }
}